import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { colors, fonts, frames } from "../../../Styles";
import { ReactiveTable, EsfStationEnhanced } from "../..";
import { useColumnConstructor } from "../../Tables/_Tables/useColumnConstructor";
import { ListData_ValueTypes, TableStyles_Types } from "../../../Definitions";
import { Graph_QL, useFeedComponents } from "../../../Services";
import { useStationById } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationSlice";
import { TerminalInfo } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getTerminalsCall";
import { dispatchFetchTerminalById, selectTerminalByIds } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/LocationFeedingSlices/terminalSlice";
import { useAppDispatch, useAppSelector } from "../../../Store";
import { isEqual } from "lodash";
import { dispatchFetchStationStatusById, useStationStatusById } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationStatusSlice";
import { fetchStationStatusByStationIdsThunk } from "../../../Store/Thunks/stationStatusByIds";
import { useStationVisitsById } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationVisitsSlice";
import { useStationVisitCountById } from "../../../Services/Agrisys_GqlDataCaller/StationCalls/StationVisitCountSlice";
import { getFeedMotorConfig } from "../StationLayouts/esfStationCore";

const _tableHeaderfont = { ...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary, padding: "0px 16px 2px 16px" };
const _tableHeaderWidthNo = 60;
const _tableHeaderWidthMotor = 150;
const _tableHeaderFeedAmount = 140;

const feedTerminalTypes = [80, 81, 83]

interface stationModal_Props {
  station: number;
}

export default function StationContent({ station: activeStation }: stationModal_Props) {
  const { t } = useTranslation();
  const feedMotorConfig = getFeedMotorConfig(t);
  const dispatch = useAppDispatch();

  const farm = useAppSelector(({activeData}) => activeData.farm) ?? "";
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm})),[farm]);

  const { call: getFeedComponentsInfo, data: feedComponentsState } = useFeedComponents(connection);

  useEffect(() => {
    getFeedComponentsInfo({fetchAmount: 50}, connection);
  }, [])

  const { isLoading, entity: station } = useStationById(activeStation);
  const terminals = useAppSelector((state) => (selectTerminalByIds(state, station?.terminals ?? [])), isEqual);

  useEffect(() => {
    if (!station?.terminals) return;
  
    station.terminals.forEach(id => {
      dispatch(dispatchFetchTerminalById(id));
    });
  }, [station?.terminals, dispatch]);

  const feedComponents = useMemo(() => feedComponentsState && feedComponentsState[domain] ? feedComponentsState[domain].data: undefined,[domain, feedComponentsState])
  const feedTerminals = terminals.filter(t => feedTerminalTypes.includes(t.fk_terminal_types ?? -1))

  const { isLoading: isStationStatusLoading, entity: stationStatus } = useStationStatusById(activeStation);
  
  // #region | Components Table
  const tableData = useMemo(() => {
    if (!terminals || terminals.length === 0) return [];

    return terminals
      .filter(t => feedTerminalTypes.includes(t.fk_terminal_types ?? -1))
      .map((td, index) => {
        const feedComp = feedComponents && feedComponents.find(c => c.id === td.fk_components)
        const feedMotor = stationStatus?.motors.find(m => m.terminalId === td.terminal_id)

        let totalConversion = undefined;
        if (td.val1_type === 2) {
          totalConversion = td.val1_min;
        }
        if (td.val2_type === 2) {
          totalConversion = td.val2_min;
        }
        if (totalConversion === undefined) {
          totalConversion = 1;
        }

        const totalAmount = feedMotor && feedMotor.totalFeedAmount !== 0 ? feedMotor.totalFeedAmount * totalConversion : 0;
        const totaldRes = totalConversion === 1 ? `${totalAmount} ${t("rotations", { defaultValue: "rotations" })}` : `${totalAmount}${t("g", { defaultValue: "g" })}`;

        const consumedAmount = feedMotor && feedMotor.consumedFeedAmount !== 0 ? feedMotor.consumedFeedAmount * totalConversion : 0;
        const consumedRes = totalConversion === 1 ? `${consumedAmount} ${t("rotations", { defaultValue: "rotations" })}` : `${consumedAmount}${t("g", { defaultValue: "g" })}`;
        
        const animalDetected = stationStatus?.animalId !== undefined && stationStatus?.animalId !== null
      return {
        id: index + 1,
        feedMotor: feedMotor ? feedMotorConfig[Number(feedMotor.status)].label : "", 
        feedComponent: feedComp ? <frames.ComponentLabel $component={feedComp}>{feedComp.name}</frames.ComponentLabel> : "", 
        totalFeedAmount: animalDetected ? totaldRes : "", 
        consumedFeedAmount: animalDetected ? consumedRes : ""
      };
    });
  }, [terminals, feedComponents, stationStatus]);
  
  const accessorData = useMemo(() => ([
    { headerValue: "", accessor: "id", type: ListData_ValueTypes.String, width: `${_tableHeaderWidthNo}px` },
    { headerValue: "", accessor: "feedMotor", type: ListData_ValueTypes.String, width: `${_tableHeaderWidthMotor}px` },
    { headerValue: "", accessor: "feedComponent", type: ListData_ValueTypes.Div },
    { headerValue: "", accessor: "totalFeedAmount", type: ListData_ValueTypes.String, width: `${_tableHeaderFeedAmount}px` },
    { headerValue: "", accessor: "consumedFeedAmount", type: ListData_ValueTypes.String, width: `${_tableHeaderFeedAmount}px` }
  ]),[])
  
  const columns = useColumnConstructor({accessorData, tableStyle: TableStyles_Types.noHeaderTable, canEdit: false });

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })
  // #endregion

  return (
    isLoading ? "" : <div style={{ width: "700px", display: "flex", flexDirection: "column", alignItems: "center"}}> 
      <div style={{ display: "grid", width: "100%", gridTemplateColumns: `${_tableHeaderWidthNo}px ${_tableHeaderWidthMotor}px 1fr ${_tableHeaderFeedAmount}px ${_tableHeaderFeedAmount}px`, gridTemplateRows: "1fr" }}>
        <p style={{ gridArea: "1/1/2/2", ..._tableHeaderfont }}>{`${t("esfWalkThroughPages.sections.number", {defaultValue: "no"})}.`}</p>
        <p style={{ gridArea: "1/2/2/3", ..._tableHeaderfont }}>{`${t("esfWalkThroughPages.sections.feedMotor", {defaultValue: "feed motor"})}`}</p>
        <p style={{ gridArea: "1/3/2/4", ..._tableHeaderfont }}>{`${t("esfWalkThroughPages.sections.feedComponents", { defaultValue: "feed components" })}`}</p>
        <p style={{ gridArea: "1/4/2/5", ..._tableHeaderfont }}>{`${t("esfWalkThroughPages.sections.available", { defaultValue: "available" })}`}</p>
        <p style={{ gridArea: "1/5/2/6", ..._tableHeaderfont }}>{`${t("esfWalkThroughPages.sections.consumed", {defaultValue: "consumed"})}`}</p>
      </div>
      <div style={{ border: "2px solid " + colors.greyTertiary, borderRadius: "12px", width: "100%" }}>{
        <ReactiveTable
          minRows={1}
          table={table}
          tableType={TableStyles_Types.noHeaderTable}
          emptyText={t("feedCurvePages.feedCurve.noComponents", { defaultValue: "No components found" })}
          renderFooter={false}
        />
      }</div>
      <div style={{marginTop: "-2px"}}><EsfStationEnhanced station={activeStation} components={true} /></div>
    </div>
  )
}